import React, {
  PropsWithChildren,
  useCallback,
  useEffect,
  useState
} from 'react';
import {
  Typography,
  InputLabel,
  Checkbox,
  Loading,
  useRightSideBar,
  showNotif
} from '@p81-common/ui';
import { WarningAttentionBox } from '../AssociateTenants/parts/shared/ui';
import { deleteRequest } from '../../../utils/requests';
import { useTranslation } from 'react-i18next';
import { createClasses } from './styles';

const initialErrorState = {
  error: false,
  message: ''
};

interface DisassociateTenantsProps {
  companyName: string;
  tenantId: string;
  tenantUUID: string;
  updateDataFn(): void;
}

type DisassociationConflict = 'TENANT_UUID_NOT_FOUND' | null;

const DisassociateTenants = ({
  tenantId,
  updateDataFn,
  tenantUUID
}: PropsWithChildren<DisassociateTenantsProps>) => {
  const [fieldError, setFieldError] = useState(initialErrorState);
  const [disassociationChecked, setDisassociationChecked] = useState(false);
  const [disassociationPending, setDisassociationPending] = useState(false);
  const [disassociationConflict, setDisassociationConflict] =
    useState<DisassociationConflict>(null);
  const { t } = useTranslation('activity');
  const { setOpenSecondary, setShowBottomActionBar } = useRightSideBar();
  const classes = createClasses();

  const handleCheckbox = () => {
    setDisassociationChecked(prev => !prev);
  };

  const handleRequestError = useCallback(
    e => {
      setDisassociationPending(false);
      if (e.response.status === 409) {
        setDisassociationConflict(e.response.data.message);
        return;
      }
      if (e.response.status === 404) {
        return setFieldError({
          error: true,
          message: t('tenantsDisassociation.errors.NOT_FOUND')
        });
      }
      setFieldError({
        error: true,
        message: t('tenantsDisassociation.errors.DISASSOCIATION_FAILED')
      });
    },
    [t]
  );

  const handleSubmit = useCallback(
    async e => {
      e.preventDefault();
      setDisassociationPending(true);
      try {
        const res = await deleteRequest('customer/cp/association', {
          tenantUUID,
          tenantId
        });
        setDisassociationPending(false);

        if (res.statusCode === 200) {
          setOpenSecondary(false);
          showNotif(t('tenantsDisassociation.SUCCESS', { tenantId }), {
            alertProps: {
              severity: 'success',
              variant: 'filled',
              style: { width: 'max-content' }
            }
          });
          updateDataFn();
        }
      } catch (e) {
        handleRequestError(e);
      }
    },
    [
      handleRequestError,
      setOpenSecondary,
      t,
      tenantId,
      tenantUUID,
      updateDataFn
    ]
  );

  const disableSubmit = useCallback(() => {
    const basicValidationFailed =
      fieldError.error ||
      !disassociationChecked ||
      disassociationPending ||
      !tenantUUID;

    if (disassociationConflict) {
      return (
        basicValidationFailed ||
        disassociationConflict === 'TENANT_UUID_NOT_FOUND'
      );
    }

    return basicValidationFailed;
  }, [
    fieldError.error,
    disassociationChecked,
    disassociationPending,
    tenantUUID,
    disassociationConflict
  ]);

  useEffect(() => {
    setShowBottomActionBar({
      okButtonProps: {
        text: t('tenantsDisassociation.BUTTON'),
        disabled: disableSubmit(),
        type: 'submit',
        onClick: e => handleSubmit(e)
      },
      cancelButtonProps: {
        text: t('buttons.CANCEL'),
        onClick: () => {
          setOpenSecondary(false);
          setShowBottomActionBar(null);
        }
      },
      isSideBar: true
    });
  }, [
    disassociationPending,
    disableSubmit,
    handleSubmit,
    setOpenSecondary,
    setShowBottomActionBar,
    t
  ]);

  return (
    <form className={classes.container} onSubmit={handleSubmit}>
      <div className={classes.attentionBoxWrapper}>
        <WarningAttentionBox
          title={t('tenantsDisassociation.attentionBox.title')}
          subtitle={t('tenantsDisassociation.attentionBox.subtitle')}
        >
          <InputLabel className={classes.attentionCheckbox}>
            <Checkbox
              checked={disassociationChecked}
              onChange={handleCheckbox}
            />
            <Typography variant="body2">
              {t('tenantsDisassociation.attentionBox.checkbox_label')}
            </Typography>
          </InputLabel>
        </WarningAttentionBox>
      </div>

      {disassociationPending && (
        <div className={classes.loader}>
          <Loading />
        </div>
      )}
    </form>
  );
};

export default DisassociateTenants;
